// src/pages/AboutUs.tsx
import { Shield, Users, Award } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCms } from '../../hooks/useCms';

// Types
interface TeamMember {
  name: string;
  role: string;
}

interface TimelineItem {
  year: string;
  event: string;
}

interface ValueItem {
  title: string;
  description: string;
}

export function AboutUs() {
  const { t } = useTranslation('about');
  const { get } = useCms('about');

  const parsePairs = (value: string) => value.split('\n').map(line => line.split('|').map(part => part.trim())).filter(parts => parts.length >= 2 && parts[0] && parts[1]);

  // Type-safe array extraction
  const fallbackTeam = t('team.members', { returnObjects: true }) as TeamMember[];
  const fallbackTimeline = t('story.timeline', { returnObjects: true }) as TimelineItem[];
  const fallbackValues = t('values.items', { returnObjects: true }) as ValueItem[];
  const teamPairs = parsePairs(get('values_team.team', ''));
  const timelinePairs = parsePairs(get('story.timeline', ''));
  const valuePairs = parsePairs(get('values_team.values', ''));
  const structuredTeam = [1, 2, 3, 4, 5, 6].map(number => ({ name: get(`values_team.team${number}Name`, ''), role: get(`values_team.team${number}Role`, '') })).filter(item => item.name && item.role);
  const structuredTimeline = [1, 2, 3, 4, 5].map(number => ({ year: get(`story.timeline${number}Year`, ''), event: get(`story.timeline${number}Event`, '') })).filter(item => item.year && item.event);
  const structuredValues = [1, 2, 3].map(number => ({ title: get(`values_team.value${number}Title`, ''), description: get(`values_team.value${number}Description`, '') })).filter(item => item.title && item.description);
  const teamMembers = structuredTeam.length ? structuredTeam : teamPairs.length ? teamPairs.map(([name, role]) => ({ name, role })) : fallbackTeam;
  const timeline = structuredTimeline.length ? structuredTimeline : timelinePairs.length ? timelinePairs.map(([year, event]) => ({ year, event })) : fallbackTimeline;
  const values = structuredValues.length ? structuredValues : valuePairs.length ? valuePairs.map(([title, description]) => ({ title, description })) : fallbackValues;

  const stats = [
    { value: get('statistics.stat1Value', '50,000+'), label: get('statistics.stat1Label', t('stats.listings')) },
    { value: get('statistics.stat2Value', '120,000+'), label: get('statistics.stat2Label', t('stats.users')) },
    { value: get('statistics.stat3Value', '15,000+'), label: get('statistics.stat3Label', t('stats.sales')) },
    { value: get('statistics.stat4Value', '98%'), label: get('statistics.stat4Label', t('stats.satisfaction')) },
  ];

  const valueIcons = [Shield, Users, Award];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-[#2D323A] to-[#1E2229] text-white py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <span className="px-3 py-1 bg-[#1E6DFF]/30 text-[#1E6DFF] rounded-full text-sm mb-4 inline-block">
            {get('header.badge', t('hero.badge'))}
          </span>
          <h1 className="text-4xl md:text-5xl text-white mb-6">
            {get('header.titleLine1', t('hero.title'))}<br />
            <span className="text-[#1E6DFF]">{get('header.titleHighlight', t('hero.title_highlight'))}</span>
          </h1>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            {get('header.description', t('hero.description'))}
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-16">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center p-6 bg-[#F5F7FA] rounded-2xl">
              <p className="text-3xl text-[#1E6DFF] mb-1">{stat.value}</p>
              <p className="text-[#717182]">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Story */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16 items-center">
          <div>
            <h2 className="text-3xl text-[#2D323A] mb-4">{get('story.storyTitle', t('story.title'))}</h2>
            <div className="space-y-4 text-[#717182] leading-relaxed">
              <p>{get('story.paragraph1', get('story.story', t('story.paragraph1')))}</p>
            </div>
          </div>
          <div className="bg-gradient-to-br from-[#1E6DFF]/10 to-[#1E6DFF]/5 rounded-2xl p-8">
            <div className="space-y-4">
              {timeline.map((item: TimelineItem, index: number) => (
                <div key={index} className="flex gap-4 items-start">
                  <span className="text-sm text-[#1E6DFF] w-10 flex-shrink-0">{item.year}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-[#1E6DFF] rounded-full flex-shrink-0 mt-1" />
                    <span className="text-sm text-[#2D323A]">{item.event}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Values */}
        <div className="mb-16">
          <h2 className="text-3xl text-[#2D323A] text-center mb-10">{get('values_team.valuesTitle', t('values.title'))}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {values.map((value: ValueItem, index: number) => {
              const Icon = valueIcons[index];
              const colors = [
                'text-[#1E6DFF] bg-[#1E6DFF]/10',
                'text-[#22C55E] bg-[#22C55E]/10',
                'text-[#F59E0B] bg-[#F59E0B]/10'
              ];
              return (
                <div key={value.title} className="bg-white border border-[#E5E7EB] rounded-2xl p-6 hover:shadow-md transition-shadow">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${colors[index]}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-[#2D323A] mb-2">{value.title}</h3>
                  <p className="text-[#717182] text-sm leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Team */}
        <div>
          <h2 className="text-3xl text-[#2D323A] text-center mb-10">{get('values_team.teamTitle', t('team.title'))}</h2>
          <div className="grid grid-cols-2  md:grid-cols-3 gap-6 sm:gap-8">
            {teamMembers.map((member: TeamMember) => (
              <div key={member.name} className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-[#1E6DFF] to-[#1557d9] rounded-2xl flex items-center justify-center text-white text-2xl mx-auto mb-3 shadow-md">
                  {member.name.charAt(0)}
                </div>
                <p className="text-[#2D323A]">{member.name}</p>
                <p className="text-sm text-[#717182]">{member.role}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
